import { Flex, Grid, Icon, Text } from "@chakra-ui/react"
import { useLanguages } from "Hooks/use-languages"
import { DeviceDesktopIcon } from "Icons/DeviceDesktopIcon"
import { IncentiveIcon } from "Icons/IncentiveIcon"
import { LanguagesIcon } from "Icons/LanguagesIcon"
import { LocationIcon } from "Icons/LocationIcon"
import { ScreenerIcon } from "Icons/ScreenerIcon"
import { TimeIcon } from "Icons/TimeIcon"
import { useTranslate } from "Shared/hooks/translations/useTranslate"
import { formatDuration } from "date-fns"
import React, { ReactNode } from "react"
import { DeviceRequirement } from "~/api/generated/usabilityhubSchemas"

type Props = {
  incentive?: ReactNode
  deviceRequirement: Omit<DeviceRequirement, "id">
  languageCode: string
  eventDurationMins: number
  hasScreener?: boolean
}
/**
 * Shows a grid of details for an interview.
 *
 * Note: `hasScreener` will only show the screener field if a boolean is provided.
 * We want to show this field only in some cases.
 */
export const InterviewDetails: React.FC<Props> = ({
  incentive,
  deviceRequirement,
  eventDurationMins,
  hasScreener,
  languageCode,
}) => {
  const t = useTranslate()
  const languages = useLanguages()

  const translatedDeviceTypes =
    deviceRequirement.device_types.length === 3
      ? [t("device_type.any_device")]
      : deviceRequirement.device_types.map((dt) => t(`device_type.${dt}`))
  const translatedPeripherals = deviceRequirement.device_peripherals.map((p) =>
    t(`device_peripheral.${p}`)
  )
  const language = languages.find((language) => language.code === languageCode)

  return (
    <Grid
      as="dl"
      templateColumns="max-content 1fr"
      rowGap={3}
      columnGap={6}
      alignItems="flex-start"
      py={3}
      color="ds.text.default"
    >
      {language && (
        <>
          <Flex as="dt" align="center" gap={2} color="ds.text.subtle">
            <Icon as={LanguagesIcon} />
            <Text>{t("interview.summary.language")}</Text>
          </Flex>
          <Text
            as="dd"
            display="inline-flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            {language.english_name}
          </Text>
        </>
      )}

      {incentive && (
        <>
          <Flex as="dt" align="center" gap={2} color="ds.text.subtle">
            <Icon as={IncentiveIcon} />
            <Text>{t("interview.summary.incentive")}</Text>
          </Flex>
          <Text as="dd" textAlign="right">
            {incentive}
          </Text>
        </>
      )}

      <Flex as="dt" align="center" gap={2} color="ds.text.subtle">
        <Icon as={TimeIcon} />
        <Text>{t("interview.summary.duration")}</Text>
      </Flex>
      <Text as="dd" textAlign="right">
        {formatDuration(
          { minutes: eventDurationMins },
          { format: ["minutes"] }
        )}
      </Text>

      <Flex as="dt" align="center" gap={2} color="ds.text.subtle">
        <Icon as={LocationIcon} />
        <Text>{t("interview.summary.location")}</Text>
      </Flex>
      <Text as="dd" textAlign="right">
        {t("interview.summary.location_online_meeting")}
      </Text>

      <Flex as="dt" align="center" gap={2} color="ds.text.subtle">
        <Icon as={DeviceDesktopIcon} />
        <Text>{t("interview.summary.device_type")}</Text>
      </Flex>
      <Text as="dd" textAlign="right">
        {translatedDeviceTypes.join(" • ")}
      </Text>

      <Flex as="dt" align="center" gap={2} color="ds.text.subtle">
        <Icon as={DeviceDesktopIcon} />
        <Text>{t("interview.summary.requirements")}</Text>
      </Flex>
      <Text as="dd" textAlign="right">
        {translatedPeripherals.join(" • ")}
      </Text>
      {hasScreener !== undefined && (
        <>
          <Flex as="dt" align="center" gap={2} color="ds.text.subtle">
            <ScreenerIcon />
            <Text>{t("interview.summary.screener")}</Text>
          </Flex>
          <Text as="dd" textAlign="right">
            {hasScreener
              ? t("interview.summary.screener_enabled")
              : t("interview.summary.screener_disabled")}
          </Text>
        </>
      )}
    </Grid>
  )
}
